/**
 * /crm/settings/routing — admin-configurable assignment + escalation matrix.
 *
 * Each row is a `RoutingRule`: when the match panel matches a ticket, the
 * engine fires tier 1 (assigns + notifies the targets), then walks the
 * ladder if the ack/no-progress clocks expire. SLA timers (response,
 * resolution, ack) live on the rule itself; per-tier wait happens on the
 * ladder. There are no hardcoded caps — admins can wire any number of
 * tiers and any combination of users + roles.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Beaker,
  Plus,
  ShieldAlert,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Combobox } from '@/components/ui/Combobox';
import { MultiCombobox } from '@/components/ui/MultiCombobox';
import { useCrmAssignees, type CrmTicketPriority, type CrmTicketType } from '@/api/bi/crm';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useRoutingRules,
  useCreateRoutingRule,
  usePatchRoutingRule,
  useDeleteRoutingRule,
  useRoutingPreview,
  type CrmRoutingRule,
  type CrmRoutingRulePayload,
  type CrmRoutingTarget,
  type CrmRoutingTier,
  type CrmRoutingSource,
} from '@/api/bi/crmSettings';
import { useStoresList } from '@/api/legacy/stores';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';

// ---------------------------------------------------------------------------
// Constants — copied from the backend so the labels are stable in the UI.
// ---------------------------------------------------------------------------

const TYPE_OPTIONS: Array<{ value: CrmTicketType; label: string }> = [
  { value: 'sla_breach', label: 'SLA Breach' },
  { value: 'customer_delay', label: 'Customer Delay' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'refund', label: 'Refund' },
  { value: 'shrinkage', label: 'Shrinkage' },
  { value: 'damage', label: 'Damage' },
];

const PRIORITY_OPTIONS: Array<{ value: CrmTicketPriority; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const SOURCE_OPTIONS: Array<{ value: CrmRoutingSource; label: string }> = [
  { value: 'auto', label: 'Auto-created' },
  { value: 'manual', label: 'Manual' },
  { value: 'api', label: 'API' },
];

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Store Manager' },
  { value: 'support', label: 'Support' },
  { value: 'operation', label: 'Operations' },
  { value: 'account', label: 'Accounts' },
  { value: 'cycle_maintainer', label: 'Cycle Maintainer' },

];

const NOTIFY_KIND_OPTIONS = [
  { value: 'auto_created', label: 'Auto-created' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'tier_advanced', label: 'Tier advanced' },
  { value: 'ack_overdue', label: 'Ack overdue' },
  { value: 'response_breached', label: 'Response breached' },
  { value: 'resolution_breached', label: 'Resolution breached' },
  { value: 'escalated', label: 'Escalated' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function secondsToMinutes(value: number | null | undefined): string {
  if (value == null) return '';
  return String(Math.round(value / 60));
}

function minutesToSeconds(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 60);
}

function emptyRule(): CrmRoutingRule {
  return {
    id: 0,
    name: '',
    description: '',
    is_enabled: true,
    evaluation_order: 100,
    match_types: [],
    match_priorities: [],
    match_sources: ['auto'],
    match_all_stores: true,
    match_store_ids: [],
    response_sla_seconds: 60 * 60,
    resolution_sla_seconds: 4 * 60 * 60,
    ack_sla_seconds: 30 * 60,
    pause_on_waiting_customer: true,
    tiers: [
      {
        position: 1,
        label: 'Tier 1',
        wait_seconds_after_previous: 0,
        notify_event_kind: 'auto_created',
        targets: [],
      },
    ],
    created_at: null,
    updated_at: null,
  };
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CrmRoutingPage() {
  const rulesQuery = useRoutingRules();
  const [editing, setEditing] = useState<CrmRoutingRule | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const sortedRules = useMemo(
    () => [...(rulesQuery.data?.results ?? [])].sort((a, b) => a.evaluation_order - b.evaluation_order),
    [rulesQuery.data],
  );

  return (
    <>
      <PageHeader
        eyebrow="CRM Settings"
        title="Routing Matrix"
        description="Configure how tickets are assigned and escalated. Rules match by ticket type, priority, store, and source — everything else is configurable per tier."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setPreviewOpen(true)}>
              <Beaker className="mr-1 h-3.5 w-3.5" /> Test a rule
            </Button>
            <Button size="sm" onClick={() => setEditing(emptyRule())}>
              <Plus className="mr-1 h-3.5 w-3.5" /> New rule
            </Button>
          </>
        }
      />

      {rulesQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : rulesQuery.isError ? (
        <ErrorState
          title="Could not load routing rules"
          description={String((rulesQuery.error as Error)?.message ?? '')}
        />
      ) : sortedRules.length === 0 ? (
        <EmptyState
          icon={<ShieldAlert className="h-5 w-5" />}
          title="No routing rules yet"
          description="Add your first rule to start auto-assigning tickets and driving SLA escalations."
        />
      ) : (
        <div className="space-y-2">
          {sortedRules.map((rule) => (
            <RuleRow key={rule.id} rule={rule} onEdit={() => setEditing(rule)} />
          ))}
        </div>
      )}

      {editing ? (
        <RuleEditorDialog rule={editing} onClose={() => setEditing(null)} />
      ) : null}

      <RulePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

function RuleRow({ rule, onEdit }: { rule: CrmRoutingRule; onEdit: () => void }) {
  return (
    <Card className="transition-shadow hover:shadow-kpi">
      <CardBody className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="truncate text-sm font-semibold">{rule.name}</h3>
            <Badge tone={rule.is_enabled ? 'success' : 'neutral'}>
              {rule.is_enabled ? 'enabled' : 'disabled'}
            </Badge>
            <Badge tone="info">priority order {rule.evaluation_order}</Badge>
          </div>
          {rule.description ? (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{rule.description}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            {rule.match_types.length === 0 ? (
              <Badge tone="neutral">all types</Badge>
            ) : (
              rule.match_types.map((t) => (
                <Badge key={t} tone="primary">
                  {t}
                </Badge>
              ))
            )}
            {rule.match_priorities.length > 0 ? (
              <Badge tone="warning">priority: {rule.match_priorities.join(', ')}</Badge>
            ) : null}
            {rule.match_sources.length > 0 && rule.match_sources.length < 3 ? (
              <Badge tone="neutral">{rule.match_sources.join('+')}</Badge>
            ) : null}
            {!rule.match_all_stores ? (
              <Badge tone="warning">{rule.match_store_ids.length} stores</Badge>
            ) : null}
            <Badge tone="neutral">{rule.tiers.length} tier{rule.tiers.length === 1 ? '' : 's'}</Badge>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 text-[11px] text-muted-foreground">
          <span>response: {secondsToMinutes(rule.response_sla_seconds) || '—'}m</span>
          <span>·</span>
          <span>resolution: {secondsToMinutes(rule.resolution_sla_seconds) || '—'}m</span>
          <span>·</span>
          <span>ack: {secondsToMinutes(rule.ack_sla_seconds) || '—'}m</span>
          <Button size="sm" variant="outline" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Editor dialog
// ---------------------------------------------------------------------------

function RuleEditorDialog({ rule, onClose }: { rule: CrmRoutingRule; onClose: () => void }) {
  const [draft, setDraft] = useState<CrmRoutingRule>(rule);
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => setDraft(rule), [rule]);
  const isNew = !rule.id;

  const create = useCreateRoutingRule();
  const patch = usePatchRoutingRule(rule.id || null);
  const remove = useDeleteRoutingRule();

  const stores = useStoresList();
  const storeOptions = useMemo(
    () => (stores.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [stores.data],
  );

  const isSubmitting = create.isPending || patch.isPending;

  function setField<K extends keyof CrmRoutingRule>(key: K, value: CrmRoutingRule[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  function setTier(idx: number, next: CrmRoutingTier) {
    setDraft((d) => {
      const tiers = [...d.tiers];
      tiers[idx] = next;
      return { ...d, tiers };
    });
  }

  function addTier() {
    setDraft((d) => {
      const nextPosition = d.tiers.length === 0 ? 1 : Math.max(...d.tiers.map((t) => t.position)) + 1;
      return {
        ...d,
        tiers: [
          ...d.tiers,
          {
            position: nextPosition,
            label: `Tier ${nextPosition}`,
            wait_seconds_after_previous: 30 * 60,
            notify_event_kind: 'tier_advanced',
            targets: [],
          },
        ],
      };
    });
  }

  function removeTier(idx: number) {
    setDraft((d) => {
      const tiers = d.tiers.filter((_, i) => i !== idx).map((t, i) => ({ ...t, position: i + 1 }));
      return { ...d, tiers };
    });
  }

  function moveTier(idx: number, direction: -1 | 1) {
    setDraft((d) => {
      const target = idx + direction;
      if (target < 0 || target >= d.tiers.length) return d;
      const tiers = [...d.tiers];
      [tiers[idx], tiers[target]] = [tiers[target], tiers[idx]];
      return { ...d, tiers: tiers.map((t, i) => ({ ...t, position: i + 1 })) };
    });
  }

  async function handleSubmit() {
    if (!draft.name.trim()) {
      notify.error('Rule name is required.');
      return;
    }
    if (draft.tiers.length === 0) {
      notify.error('Add at least one tier.');
      return;
    }
    for (let i = 0; i < draft.tiers.length; i += 1) {
      const tier = draft.tiers[i];
      if (tier.targets.length === 0) {
        notify.error(`Tier ${tier.position} needs at least one target.`);
        return;
      }
      for (const target of tier.targets) {
        if (!target.notify_in_app && !target.notify_email && !target.notify_whatsapp) {
          notify.error(`Tier ${tier.position} has a target with no notification channels.`);
          return;
        }
      }
    }
    const payload: CrmRoutingRulePayload = {
      name: draft.name.trim(),
      description: draft.description,
      is_enabled: draft.is_enabled,
      evaluation_order: draft.evaluation_order,
      match_types: draft.match_types,
      match_priorities: draft.match_priorities,
      match_sources: draft.match_sources,
      match_all_stores: draft.match_all_stores,
      match_store_ids: draft.match_all_stores ? [] : draft.match_store_ids,
      response_sla_seconds: draft.response_sla_seconds,
      resolution_sla_seconds: draft.resolution_sla_seconds,
      ack_sla_seconds: draft.ack_sla_seconds,
      pause_on_waiting_customer: draft.pause_on_waiting_customer,
      tiers: draft.tiers.map((t, idx) => ({
        ...t,
        position: idx + 1,
        targets: t.targets.map((tg) => ({ ...tg })),
      })),
    };
    try {
      if (isNew) {
        await create.mutateAsync(payload);
        notify.success('Rule created.');
      } else {
        await patch.mutateAsync(payload);
        notify.success('Rule updated.');
      }
      onClose();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save rule.'));
    }
  }

  async function handleDelete() {
    if (!rule.id) return;
    try {
      await remove.mutateAsync(rule.id);
      notify.success('Rule deleted.');
      setConfirmDelete(false);
      onClose();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not delete rule.'));
    }
  }

  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : null)}>
      <DialogContent className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
        <DialogTitle className="text-base font-semibold">
          {isNew ? 'New routing rule' : `Edit “${rule.name}”`}
        </DialogTitle>

        <section className="mt-4 space-y-3">
          <Field label="Name">
            <Input
              value={draft.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g. SLA breach (default)"
            />
          </Field>
          <Field label="Description" hint="Optional — visible only inside the BI console.">
            <Input
              value={draft.description}
              onChange={(e) => setField('description', e.target.value)}
              placeholder="Auto-route SLA breach tickets to the store manager."
            />
          </Field>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Evaluation order" hint="Lower values match first.">
              <Input
                type="number"
                value={draft.evaluation_order}
                onChange={(e) => setField('evaluation_order', Number(e.target.value) || 0)}
              />
            </Field>
            <Field label="Status">
              <Select
                value={draft.is_enabled ? '1' : '0'}
                onValueChange={(v) => setField('is_enabled', v === '1')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Enabled</SelectItem>
                  <SelectItem value="0">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
        </section>

        <section className="mt-6 space-y-3 rounded-md border border-border bg-surface px-3 py-3">
          <h3 className="text-sm font-semibold">Match conditions</h3>
          <Field label="Ticket types" hint="Empty = match every type.">
            <MultiCombobox
              values={draft.match_types}
              onChange={(values) => setField('match_types', values as CrmTicketType[])}
              options={TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="All types"
              allLabel="All types"
            />
          </Field>
          <Field label="Priorities" hint="Empty = match every priority.">
            <MultiCombobox
              values={draft.match_priorities}
              onChange={(values) => setField('match_priorities', values as CrmTicketPriority[])}
              options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="All priorities"
              allLabel="All priorities"
            />
          </Field>
          <Field label="Sources" hint="Auto = engine-created (e.g. SLA breach), Manual = staff click, API = service-to-service.">
            <MultiCombobox
              values={draft.match_sources}
              onChange={(values) => setField('match_sources', values as CrmRoutingSource[])}
              options={SOURCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              placeholder="All sources"
              allLabel="All sources"
            />
          </Field>
          <Field label="Store scope">
            <Select
              value={draft.match_all_stores ? 'all' : 'list'}
              onValueChange={(v) => setField('match_all_stores', v === 'all')}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stores</SelectItem>
                <SelectItem value="list">Specific stores…</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {!draft.match_all_stores ? (
            <Field label="Pick stores" hint="Rule only fires when the ticket's store is on this list.">
              <MultiCombobox
                values={draft.match_store_ids.map(String)}
                onChange={(values) =>
                  setField(
                    'match_store_ids',
                    values.map((v) => Number(v)).filter((n) => Number.isFinite(n)),
                  )
                }
                options={storeOptions}
                placeholder={stores.isLoading ? 'Loading…' : 'Select stores'}
                allLabel="No stores"
              />
            </Field>
          ) : null}
        </section>

        <section className="mt-6 space-y-3 rounded-md border border-border bg-surface px-3 py-3">
          <h3 className="text-sm font-semibold">SLA timers (minutes — leave blank to disable)</h3>
          <div className="grid gap-3 md:grid-cols-3">
            <Field label="Response SLA" hint="First interaction target.">
              <Input
                type="number"
                value={secondsToMinutes(draft.response_sla_seconds)}
                onChange={(e) => setField('response_sla_seconds', minutesToSeconds(e.target.value))}
              />
            </Field>
            <Field label="Resolution SLA" hint="Hard target before a breach event fires.">
              <Input
                type="number"
                value={secondsToMinutes(draft.resolution_sla_seconds)}
                onChange={(e) => setField('resolution_sla_seconds', minutesToSeconds(e.target.value))}
              />
            </Field>
            <Field label="Ack SLA" hint="If primary doesn't ack in this window, the next tier fires.">
              <Input
                type="number"
                value={secondsToMinutes(draft.ack_sla_seconds)}
                onChange={(e) => setField('ack_sla_seconds', minutesToSeconds(e.target.value))}
              />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={draft.pause_on_waiting_customer}
              onChange={(e) => setField('pause_on_waiting_customer', e.target.checked)}
            />
            <span>Pause SLA clocks while ticket is in “Waiting on customer”</span>
          </label>
        </section>

        <section className="mt-6 space-y-3 rounded-md border border-border bg-surface px-3 py-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Escalation tiers</h3>
            <Button size="sm" variant="outline" onClick={addTier}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add tier
            </Button>
          </div>
          {draft.tiers.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              Add at least one tier; tier 1 fires immediately when the rule matches.
            </p>
          ) : null}
          {draft.tiers.map((tier, idx) => (
            <TierEditor
              key={`${tier.id ?? 'new'}-${idx}`}
              tier={tier}
              tierIndex={idx}
              onChange={(next) => setTier(idx, next)}
              onMoveUp={() => moveTier(idx, -1)}
              onMoveDown={() => moveTier(idx, 1)}
              canMoveUp={idx > 0}
              canMoveDown={idx < draft.tiers.length - 1}
              onRemove={() => removeTier(idx)}
            />
          ))}
        </section>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div>
            {!isNew ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="text-danger"
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete rule
              </Button>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={isSubmitting}>
              {isNew ? 'Create rule' : 'Save changes'}
            </Button>
          </div>
        </div>
      </DialogContent>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete “${rule.name}”?`}
        description="Tickets matching this rule fall through to the next one in evaluation order, or to the default assignee if none match."
        confirmLabel="Delete rule"
        tone="destructive"
        isPending={remove.isPending}
        onConfirm={() => void handleDelete()}
      />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Tier editor
// ---------------------------------------------------------------------------

function TierEditor({
  tier,
  tierIndex,
  onChange,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  onRemove,
}: {
  tier: CrmRoutingTier;
  tierIndex: number;
  onChange: (tier: CrmRoutingTier) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onRemove: () => void;
}) {
  function setTargets(targets: CrmRoutingTarget[]) {
    onChange({ ...tier, targets });
  }

  function addUserTarget() {
    setTargets([
      ...tier.targets,
      {
        kind: 'user',
        user_id: null,
        role: '',
        is_primary: tier.targets.length === 0,
        is_watcher: false,
        notify_in_app: true,
        notify_email: true,
        notify_whatsapp: false,
      },
    ]);
  }

  function addRoleTarget() {
    setTargets([
      ...tier.targets,
      {
        kind: 'role',
        user_id: null,
        role: 'manager',
        is_primary: tier.targets.length === 0,
        is_watcher: false,
        notify_in_app: true,
        notify_email: true,
        notify_whatsapp: false,
      },
    ]);
  }

  function setTarget(idx: number, next: CrmRoutingTarget) {
    const out = [...tier.targets];
    out[idx] = next;
    setTargets(out);
  }

  function removeTarget(idx: number) {
    setTargets(tier.targets.filter((_, i) => i !== idx));
  }

  return (
    <div className="space-y-3 rounded-md border border-border bg-surface-elevated p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="primary">Tier {tierIndex + 1}</Badge>
        <Input
          className="max-w-xs"
          value={tier.label}
          placeholder="Tier label"
          onChange={(e) => onChange({ ...tier, label: e.target.value })}
        />
        <Input
          type="number"
          className="max-w-[110px]"
          value={Math.round(tier.wait_seconds_after_previous / 60)}
          placeholder="wait (min)"
          onChange={(e) =>
            onChange({
              ...tier,
              wait_seconds_after_previous: Math.max(0, Math.round(Number(e.target.value) || 0) * 60),
            })
          }
          disabled={tierIndex === 0}
          title={tierIndex === 0 ? 'Tier 1 fires immediately' : undefined}
        />
        <span className="text-[11px] text-muted-foreground">min after previous tier</span>
        <Select
          value={tier.notify_event_kind}
          onValueChange={(v) => onChange({ ...tier, notify_event_kind: v })}
        >
          <SelectTrigger className="h-8 w-[180px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NOTIFY_KIND_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onMoveUp} disabled={!canMoveUp}>
            <ArrowUp className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onMoveDown} disabled={!canMoveDown}>
            <ArrowDown className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" className="text-danger" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {tier.targets.map((target, idx) => (
          <TargetRow
            key={`${target.id ?? 'new'}-${idx}`}
            target={target}
            onChange={(next) => setTarget(idx, next)}
            onRemove={() => removeTarget(idx)}
          />
        ))}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addUserTarget}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Specific user
          </Button>
          <Button size="sm" variant="outline" onClick={addRoleTarget}>
            <Plus className="mr-1 h-3.5 w-3.5" /> Role
          </Button>
        </div>
      </div>
    </div>
  );
}

function TargetRow({
  target,
  onChange,
  onRemove,
}: {
  target: CrmRoutingTarget;
  onChange: (target: CrmRoutingTarget) => void;
  onRemove: () => void;
}) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const assignees = useCrmAssignees(debouncedQuery);

  const userOptions = useMemo(() => {
    const map = new Map<string, { value: string; label: string; hint?: string }>();
    if (target.user_id != null) {
      map.set(String(target.user_id), {
        value: String(target.user_id),
        label: target.user_name ?? `User #${target.user_id}`,
        hint: target.user_email ?? undefined,
      });
    }
    for (const u of assignees.data?.results ?? []) {
      map.set(String(u.id), {
        value: String(u.id),
        label: u.name,
        hint: u.role ?? u.phone ?? undefined,
      });
    }
    return Array.from(map.values());
  }, [assignees.data, target.user_id, target.user_name, target.user_email]);

  const isUser = target.kind === 'user';
  const totalAssignees = assignees.data?.pagination.total;
  const emptyMessage =
    debouncedQuery.trim()
      ? 'No matching users'
      : totalAssignees != null && totalAssignees > (assignees.data?.results.length ?? 0)
        ? `Showing first ${assignees.data?.results.length ?? 0} of ${totalAssignees} — type to search`
        : 'No assignable users';

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface px-2 py-1.5 text-xs">
      <Badge tone={isUser ? 'info' : 'primary'}>{isUser ? 'user' : 'role'}</Badge>
      {isUser ? (
        <Combobox
          value={target.user_id != null ? String(target.user_id) : null}
          onChange={(v) => onChange({ ...target, user_id: v != null ? Number(v) : null })}
          options={userOptions}
          placeholder="Pick user"
          searchPlaceholder="Search by name / phone"
          emptyMessage={emptyMessage}
          showClearOption={false}
          loading={assignees.isLoading}
          onSearchChange={setQuery}
        />
      ) : (
        <Select
          value={target.role}
          onValueChange={(v) => onChange({ ...target, role: v })}
        >
          <SelectTrigger className="h-8 w-[180px] text-xs">
            <SelectValue placeholder="Pick role" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <label
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1',
          target.is_primary && 'border-primary/40 bg-primary/10 text-primary',
        )}
      >
        <input
          type="checkbox"
          checked={target.is_primary}
          onChange={(e) =>
            onChange({
              ...target,
              is_primary: e.target.checked,
              is_watcher: e.target.checked ? false : target.is_watcher,
            })
          }
        />
        <span>primary</span>
      </label>
      <label
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1',
          target.is_watcher && 'border-warning/40 bg-warning/10 text-warning-soft',
        )}
      >
        <input
          type="checkbox"
          checked={target.is_watcher}
          onChange={(e) => {
            const enablingWatcher = e.target.checked;
            onChange({
              ...target,
              is_watcher: enablingWatcher,
              is_primary: enablingWatcher ? false : target.is_primary,
              // Mirror the backend default for watcher rows: email-only
              // unless an admin explicitly re-enables the other channels.
              notify_in_app: enablingWatcher ? false : target.notify_in_app,
              notify_email: enablingWatcher ? true : target.notify_email,
            });
          }}
        />
        <span>watcher</span>
      </label>
      <ChannelToggle
        label="In-app"
        checked={target.notify_in_app}
        onChange={(checked) => onChange({ ...target, notify_in_app: checked })}
      />
      <ChannelToggle
        label="Email"
        checked={target.notify_email}
        onChange={(checked) => onChange({ ...target, notify_email: checked })}
      />
      <ChannelToggle
        label="WhatsApp"
        checked={target.notify_whatsapp}
        onChange={(checked) => onChange({ ...target, notify_whatsapp: checked })}
      />
      {isUser ? (
        <input
          type="search"
          className="hidden"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      ) : null}
      <Button size="sm" variant="ghost" className="ml-auto text-danger" onClick={onRemove}>
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function ChannelToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1',
        checked && 'border-primary/40 bg-primary/10 text-primary',
      )}
      title="Auto routing only — manual actions always send all enabled global channels."
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Preview dialog
// ---------------------------------------------------------------------------

function RulePreviewDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [type, setType] = useState<CrmTicketType>('sla_breach');
  const [priority, setPriority] = useState<CrmTicketPriority>('normal');
  const [source, setSource] = useState<CrmRoutingSource>('auto');
  const [storeId, setStoreId] = useState<string | null>(null);
  const stores = useStoresList();
  const preview = useRoutingPreview();

  const storeOptions = useMemo(
    () => (stores.data ?? []).map((s) => ({ value: String(s.id), label: s.name })),
    [stores.data],
  );

  async function run() {
    try {
      await preview.mutateAsync({
        type,
        priority,
        source,
        store_id: storeId ? Number(storeId) : null,
      });
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not run preview.'));
    }
  }

  const result = preview.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogTitle className="text-base font-semibold">Test routing rule resolution</DialogTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Pick a ticket shape; we'll show which rule would match and who would be paged at tier 1.
          No tickets are created.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <Field label="Ticket type">
            <Select value={type} onValueChange={(v) => setType(v as CrmTicketType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Priority">
            <Select value={priority} onValueChange={(v) => setPriority(v as CrmTicketPriority)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Source">
            <Select value={source} onValueChange={(v) => setSource(v as CrmRoutingSource)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Store (optional)">
            <Combobox
              value={storeId}
              onChange={setStoreId}
              options={storeOptions}
              placeholder="Any store"
              clearLabel="Any store"
            />
          </Field>
        </div>
        <Button className="mt-4" size="sm" onClick={run} disabled={preview.isPending}>
          Run preview
        </Button>
        {result ? (
          <div className="mt-4 space-y-2 rounded-md border border-border bg-surface px-3 py-3 text-xs">
            {result.rule_id ? (
              <>
                <p>
                  Matched rule <span className="font-semibold">#{result.rule_id}</span>{' '}
                  ({result.rule_name})
                </p>
                <p>
                  Tier {result.tier_position} would page{' '}
                  {result.assignee_user_ids.length} assignee
                  {result.assignee_user_ids.length === 1 ? '' : 's'} (primary user{' '}
                  {result.primary_user_id ?? 'none'})
                </p>
                <p className="text-muted-foreground">
                  Channels: in-app {result.in_app_user_ids.length} · email{' '}
                  {result.email_user_ids.length} · WhatsApp {result.whatsapp_user_ids.length}
                </p>
                <p className="text-muted-foreground">
                  Response SLA {secondsToMinutes(result.response_sla_seconds) || '—'}m · Resolution{' '}
                  {secondsToMinutes(result.resolution_sla_seconds) || '—'}m · Ack{' '}
                  {secondsToMinutes(result.ack_sla_seconds) || '—'}m
                </p>
              </>
            ) : (
              <div className="flex items-center gap-2 text-warning">
                <AlertTriangle className="h-4 w-4" /> No rule matches this ticket.
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

